"use client";

import { getBrands, getVariantByDetailedSlugs } from "@/app/utilities/apis";
import type { Brand, Model, SelectedCar, Variant } from "@/app/utilities/compareTypes";
import { formatPrice, generateCompareURLByDetailedSlugs, safeImageUrl } from "@/app/utilities/helper";
import { buildVehicleToken } from "@/app/utilities/slug";
import CarSelector from "@/component/ui/CarSelector";
import ImageWrapper from "@/component/ui/ImageWrapper";
import LeadButton from "@/component/ui/Lead/LeadButton";
import Cookies from 'js-cookie';
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useState } from "react";

interface ClientWrapperProps {
	initialSelectedFullVariants?: (Variant | null)[];
	initialSelectedCars?: (SelectedCar | null)[];
	heading?: string;
	inputTrackingClass?: string;
	btnClickTrackingClass?: string;
	editClickTrackingClass?: string;
	removeClickTrackingClass?: string;
}

export default function ClientWrapper({
	initialSelectedFullVariants = [null, null, null, null],
	initialSelectedCars = [null, null, null, null],
	heading,
	inputTrackingClass = "",
	btnClickTrackingClass = "",
	editClickTrackingClass = "",
	removeClickTrackingClass = "",
}: ClientWrapperProps) {
	const router = useRouter();

	// track mobile viewport to change Add Cars behavior on small screens
	const [isMobile, setIsMobile] = useState<boolean>(false);

	useEffect(() => {
		const check = () => setIsMobile(typeof window !== 'undefined' ? window.innerWidth < 768 : false);
		check();
		window.addEventListener('resize', check);
		return () => window.removeEventListener('resize', check);
	}, []);

	const [brands, setBrands] = useState<Brand[]>([]);
	const [brandsLoading, setBrandsLoading] = useState(false);
	const [brandsFetched, setBrandsFetched] = useState(false);
	const [sponsoredVariant, setSponsoredVariant] = useState<any | null>(null);
	const [showModalIndex, setShowModalIndex] = useState<number | null>(null);

	// Main state
	const [selectedCars, setSelectedCars] = useState<(SelectedCar | null)[]>(initialSelectedCars);
	const [selectedFullVariants, setSelectedFullVariants] = useState<(Variant | null)[]>(initialSelectedFullVariants);
	// Per-slot cached data for CarSelector
	const [slotModels, setSlotModels] = useState<Model[][]>([[], [], [], []]);
	const [slotVariants, setSlotVariants] = useState<Variant[][]>([[], [], [], []]);

	// Fetch brands lazily — only when user first opens the car selector modal
	useEffect(() => {
		if (showModalIndex === null || brandsFetched || brandsLoading) return;
		const fetchBrands = async () => {
			setBrandsLoading(true);
			try {
				const res = await getBrands({ page: 1, limit: 100, is_active: true, sort: "asc" });
				if (res && res.status === "success") setBrands(res.data || []);
			} catch (e) {
				console.error(e);
			} finally {
				setBrandsLoading(false);
				setBrandsFetched(true);
			}
		};
		fetchBrands();
	}, [showModalIndex, brandsFetched, brandsLoading]);

	// Fetch sponsored variant once
	useEffect(() => {
		const fetchSponsored = async () => {
			try {
				const v = await getVariantByDetailedSlugs("mahindra-scorpio-n", "z8s-petrol-mt");
				if (v) setSponsoredVariant({ ...v, sponsored: true } as Variant);
			} catch (e) {
				// ignore
			}
		};

		fetchSponsored();
	}, []);

	// Inject sponsored variant into an available slot. Prefer not to place
	// the sponsored ad in the first slot (index 0). Prefer slots 1..3,
	// falling back to 0 only if nothing else is available.
	useEffect(() => {
		if (!sponsoredVariant) return;

		const hasUserSelection = selectedCars.some(Boolean);
		const isResultPage = initialSelectedCars.some(Boolean);

		const isSlotAvailable = (i: number) => {
			const originallyPicked = Boolean(initialSelectedCars[i]);
			const v = selectedFullVariants[i];
			const currentlyPickedDifferent = !!(v && v._id && v._id !== sponsoredVariant._id);
			return !originallyPicked && !currentlyPickedDifferent;
		};

		// determine preferred slot order
		let preferredOrder: number[] = [1, 2, 3, 0];
		if (isResultPage) preferredOrder = [1, 2, 3, 0];

		const findAvailableIndex = () => {
			for (const i of preferredOrder) {
				if (isSlotAvailable(i)) return i;
			}
			return -1;
		};

		const availableIndex = findAvailableIndex();
		const alreadyInjectedAnywhere = selectedFullVariants.some((v) => v?._id === sponsoredVariant._id);

		if (hasUserSelection && availableIndex !== -1 && !alreadyInjectedAnywhere) {
			setSelectedFullVariants((prev) => {
				const next = [...prev];
				next[availableIndex] = sponsoredVariant;
				return next;
			});

			try {
				const detailedSlug = sponsoredVariant.model?.detailed_slug || "";
				// e.g., "kia-seltos-gtx-plus-diesel-at"

				const variantName = (sponsoredVariant as any).slug || sponsoredVariant.name || "";
				// e.g., "GTX Plus Diesel AT" or existing slug

				const token = buildVehicleToken({
					detailedSlug,     // Preferred: extracts "gtx-plus-diesel-at"
					variant: variantName  // Fallback: sanitizes to "gtx-plus-diesel-at"
				});

				// Token will now be something like: "gtx-plus-diesel-at" or "hte-o-turbo"
				const maxAge = 60 * 60 * 24 * 30; // 30 days
				document.cookie = `sponsored_variant=${encodeURIComponent(token)}; Path=/; Max-Age=${maxAge}; SameSite=Lax`;
			} catch (e) {
				// ignore cookie errors
			}
		}

		// Remove sponsored if user clears all selections
		if (!hasUserSelection && alreadyInjectedAnywhere) {
			setSelectedCars((prev) => prev.map((c) => (c && c.brand_slug === sponsoredVariant.brand?._id && c.variant_slug === sponsoredVariant.name ? null : c)));
			setSelectedFullVariants((prev) => prev.map((v) => (v && v._id === sponsoredVariant._id ? null : v)));
			try {
				document.cookie = `sponsored_variant=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
			} catch (e) {
				// ignore
			}
		}
	}, [sponsoredVariant, selectedCars, initialSelectedCars, selectedFullVariants]);

	// Handle car selection
	const handleSelect = useCallback((
		index: number,
		SelectedCar: SelectedCar,
		fullVariant: Variant
	) => {
		
		// If selecting the sponsored variant again, clear it instead
		if (sponsoredVariant && fullVariant._id === sponsoredVariant._id) {
			setSponsoredVariant(null);
			try {
				document.cookie = `sponsored_variant=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
			} catch (e) {
				// ignore
			}
		}

		const isDuplicate = selectedFullVariants.some(
			(v, i) => i !== index && v?._id === fullVariant._id
		);
		if (isDuplicate) {
			alert("You can't compare the same variant twice!");
			return;
		}

		setSelectedCars((prev) => {
			const next = [...prev];
			next[index] = SelectedCar;
			return next;
		});

		setSelectedFullVariants((prev) => {
			const next = [...prev];
			next[index] = {
				...fullVariant,
				name: fullVariant.name || "",
				slug: fullVariant.slug || "",
			} as Variant;
			return next;
		});

		setShowModalIndex(null);
	}, [selectedFullVariants, sponsoredVariant]);

	// Handle clearing a slot
	// Remove from sessionStorage and clear from state
	const clearSlot = useCallback(
		(idx: number, detailed_slug: string, variantSlug: string) => {

			// Remove from cookies
			try {
				const raw = Cookies.get('selected_variant_slugs');

				if (raw) {

					const parsed = JSON.parse(raw);
					if (Array.isArray(parsed)) {

						const key = `${detailed_slug}+${variantSlug}`;
						const updated = parsed.filter(
							(item: string) => item !== key
						);

						Cookies.set('selected_variant_slugs', JSON.stringify(updated), { path: '/' });
					}
				}
			} catch (error) {
				console.error('Error clearing slot from cookies:', error);
			}

			// Clear selectedCars slot
			setSelectedCars((prev) => {
				const next = [...prev];
				next[idx] = null;
				return next;
			});

			// Clear selectedFullVariants slot
			setSelectedFullVariants((prev) => {
				const next = [...prev];
				next[idx] = null;
				return next;
			});

		},
		[]
	);


	// Final display variants: on result pages keep sponsored where injected; otherwise
	// preserve existing behavior (fallback: attempt to move sponsored to slot 4).
	const isResultPage = initialSelectedCars.some(Boolean);
	const displayVariants = useMemo(() => {

		// No sponsored variant, return as-is
		if (!sponsoredVariant) return selectedFullVariants;

		const arr = [...selectedFullVariants];
		// If result page, keep the sponsored where it was injected but ensure it's flagged
		if (isResultPage) return arr.map((v) => (v && v._id === sponsoredVariant._id ? { ...v, sponsored: true } : v));

		const sponsorId = sponsoredVariant._id;
		const sponsorIndex = arr.findIndex((v) => v?._id === sponsorId);

		// Move sponsored to slot 4 if it's not already there and slot 4 isn't user-picked
		if (sponsorIndex !== -1 && sponsorIndex !== 3) {
			arr[sponsorIndex] = null;
			if (!arr[3] || arr[3]._id === sponsorId) {
				arr[3] = sponsoredVariant;
			}
		}

		// ensure any sponsored variant (from server or injected) carries the flag
		return arr.map((v) => (v && v._id === sponsoredVariant._id ? { ...v, sponsored: true } : v));
	}, [selectedFullVariants, sponsoredVariant, isResultPage]);

	// Map display variants to SelectedCar for rendering
	const displayCars = useMemo(() => {
		return displayVariants.map((v) => {

			if (!v) return null;
			return {
				_id: v._id,
				brand_name: v.brand?.name || "",
				brand_slug: v.brand?.slug || "",
				model_id: v.model?._id,
				model_name: v.model?.name || "",
				model_slug: v.model?.slug || "",
				detailed_slug: v.model?.detailed_slug,
				featured_image: v.model?.featured_image || "",
				variant_name: v.name || "",
				variant_slug: v.slug || "",
				price: v.price,
				sponsored: v.sponsored || false,
				is_discontinued: v.status === 'discontinued' || v.model?.is_discontinued || false,
				is_upcoming: v.status === 'upcoming' || v.model?.is_upcoming || false,
			} as SelectedCar;
		});
	}, [displayVariants]);

	// Handle Compare button
	const handleCompare = () => {
		// Build slug from user-selected cars using the correct structure
		const manualVariants = selectedCars
			.filter((car): car is SelectedCar => !!car && !!car.detailed_slug) // Ensure car and detailed_slug exist
			.map((car) => car.detailed_slug as string);

		const slug = generateCompareURLByDetailedSlugs(manualVariants);
		if (slug) router.push(`/compare-cars/${slug}`);
	};

	// Compute indices for Add Cars behavior
	const firstEmptyIndex = selectedCars.findIndex((c) => !c);
	const firstReplaceableIndex = selectedCars.findIndex((c, i) => {
		const hasSponsored = !!(selectedFullVariants[i] && (selectedFullVariants[i] as any).sponsored);
		return !c || hasSponsored;
	});
	// On mobile we DON'T want to replace sponsored slots — only allow truly empty slots
	const addCarsTargetIndex = isMobile ? firstEmptyIndex : firstReplaceableIndex;
	const addCarsDisabled = addCarsTargetIndex === -1;

	// Render a single slot card (either populated or placeholder)
	const renderSlot = (idx: number) => {

		const car = displayCars[idx];
		const isSponsored = !!(car?.sponsored && idx !== 0);

		// If no user-selected cars exist, shorten placeholder height by removing `h-64`.
		const noUserSelected = selectedCars.every((c) => !c);
		if (car) {

			const brandSlug = car?.brand_slug;
			const modelSlug = car?.model_slug;
			const href = brandSlug && modelSlug ? `/${brandSlug}/${modelSlug}` : '#';

			return (
				<div key={idx} className={`flex-shrink-0 w-56 md:w-auto md:mr-0 snap-start group relative overflow-hidden rounded-2xl border-2 transition-all duration-200
										${car ? 'border-gray-200 bg-white shadow-lg' : 'border-dashed border-gray-300 bg-gray-50'}
										${!isSponsored ? '' : 'cursor-default'}`}>
					<div className="relative">

						<Link href={href} title={`${car.brand_name} ${car.model_name} ${car.variant_name}`} className="w-full block overflow-hidden" prefetch={false}>
							<ImageWrapper src={safeImageUrl(car.featured_image, "")} alt={`${car.brand_name} ${car.model_name} ${car.variant_name}`} title={`${car.brand_name} ${car.model_name} ${car.variant_name}`} width={550} height={300} className="w-full h-40 md:h-48 object-cover cursor-pointer" />
						</Link>

						{!isSponsored && (
							<>
								<button onClick={(e) => { e.stopPropagation(); clearSlot(idx, car.detailed_slug || '', car.variant_slug || ''); }} className={`absolute top-3 right-3 bg-black/70 text-white p-2 rounded-full opacity-100 md:opacity-0 group-hover:opacity-100 transition z-10 cursor-pointer ${removeClickTrackingClass}`}>
									<Image
										src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}lib-icon/cross-icon-new.svg`}
										alt="cross icon"
										title="cross icon"
										width={16} 
										height={16}
										className="invert"
										/>
								</button>
								<button onClick={(e) => { e.stopPropagation(); setShowModalIndex(idx); }} className={`absolute top-3 left-3 bg-black/70 text-white px-3 py-1.5 rounded-md text-xs md:text-sm font-medium flex items-center gap-1 opacity-100 md:opacity-0 group-hover:opacity-100 transition z-10 cursor-pointer ${editClickTrackingClass}`}>
									<Image
										src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}lib-icon/edit-pencil.svg`}
										alt="cross icon"
										title="cross icon"
										width={16} 
										height={16}
									/>
									Edit
								</button>
							</>
						)}

						{isSponsored && (
							<span className="absolute top-3 left-3 bg-gray-700 text-white text-xs md:text-sm font-semibold px-2 py-1 rounded-md shadow">AD</span>
						)}
					</div>

					<div className="p-4">
						<p className="text-[13px] text-gray-500">{car.brand_name}</p>
						<p className="font-bold text-[sm] md:text-lg text-gray-900 line-clamp-2">{car.model_name}</p>
						<p className="font-bold text-[12px] md:text-[14px] text-gray-500 line-clamp-2">{car.variant_name}</p>

						{car.price && (
							<p className="mt-2 text-base md:text-lg font-bold text-gray-600">{formatPrice(car.price)}</p>
						)}

						{car.is_discontinued ? (
							<button
								disabled
								className=" mt-4 w-full py-2 text-[14px] border border-red-500 text-red-500 bg-red-50 rounded-lg text-sm md:text-base font-medium cursor-not-allowed"
							>
								Discontinued
							</button>
						) : (
							<div onClick={(e) => e.stopPropagation()} className="mt-4">
								<LeadButton  carId={car?.model_id ?? ""} variantId={car?._id ?? ""} carName={car.model_name} btnText="Get On Road Price" fullWidth className="w-full bg-teal-600 hover:bg-teal-700 text-white py-2.5 rounded-lg text-sm md:text-base font-medium cursor-pointer" is_upcoming={car.is_upcoming} datatestid={`get-on-road-price`}/>
							</div>
						)}
					</div>
				</div>
			);

		}

		return (
			<div
				key={idx}
				onClick={() => setShowModalIndex(idx)}
				className={`${noUserSelected ? "h-64" : ""} px-6 cursor-pointer border-2 border-dashed border-gray-300 bg-gray-50 rounded-2xl flex-shrink-0 w-56 md:w-auto md:mr-0 snap-start ${inputTrackingClass}`}
				
			>
				<div className="w-full h-full flex flex-col items-center justify-center">
					<div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center text-2xl md:text-3xl text-gray-400 border border-gray-200 shadow-sm">
						<span className="align-middle">+</span>
					</div>

					<p className="text-sm md:text-base font-medium text-gray-600 text-center mt-4">Add Car {idx + 1}</p>
				</div>
			</div>
		);
	};

	return (
		<>
			<div className="py-2">
				<div className="text-left md:text-center mb-10">
					<h2
						className="text-[20px] md:text-[36px] leading-[24px] md:leading-[40px] tracking-[0%] md:tracking-[-0.9px] md:text-center font-bold md:font-semibold text-gray-900 align-middle"
						
					>
						{heading ?? "Compare Cars"}
					</h2>
					<p
						className="mt-2 text-[15px] md:text-[16px] leading-[24px] font-normal tracking-[0px] text-gray-600 align-middle md:text-center"
						
					>
						Select up to 4 cars to compare specs, price & features
					</p>
				</div>

<div className="flex md:grid md:grid-cols-4 gap-6 overflow-x-auto md:overflow-visible snap-x snap-mandatory md:snap-none">
				{[0, 1, 2, 3].map(renderSlot)}
			</div>
				
			</div>
			<div className="mt-4 md:hidden">
				<div className="flex justify-center">
					{(() => {
						const sponsoredPresent = selectedFullVariants.some((v, i) => !!(v && (v as any).sponsored && i !== 0));
						const realFilledCount = selectedFullVariants.filter((v, i) => !!v && !((v as any).sponsored && i !== 0)).length;
						const disabled = addCarsDisabled || (sponsoredPresent && realFilledCount >= 3);
						return (
							<button
								type="button"
								onClick={() => {
									if (disabled) return;
									setShowModalIndex(addCarsTargetIndex === -1 ? 0 : addCarsTargetIndex);
								}}
								disabled={disabled}
								className={`w-full md:w-56 px-4 py-2.5 rounded-md border font-medium flex items-center justify-center gap-2 ${disabled ? 'border-gray-200 bg-gray-100 text-gray-500 cursor-not-allowed' : 'border-teal-600 bg-white text-teal-700'}`}
							>
								<span className="text-lg font-semibold">+</span>
								<span className="text-sm">Add Cars</span>
							</button>
						);
					})()}
				</div>
			</div>

			<div className="text-center mt-6 md:mt-12">
				<button
					onClick={handleCompare}
					// Enable only when user has manually selected at least 2 cars
					disabled={selectedCars.filter(Boolean).length < 2}
					className={`w-full md:w-100 rounded-lg px-8 py-3 font-semibold text-base md:text-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-teal-400 cursor-pointer ${btnClickTrackingClass} ${selectedCars.filter(Boolean).length >= 2 ? 'bg-teal-600 hover:bg-teal-700 text-white shadow-lg' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
				>
					<span className="block">{selectedCars.filter(Boolean).length >= 2 ? "Compare Now" : "Compare Now"}</span>
				</button>
			</div>

			{showModalIndex !== null && (
				<CarSelector
					index={showModalIndex}
					brands={brands}
					models={slotModels[showModalIndex] ?? []}
					variants={slotVariants[showModalIndex] ?? []}
					setModels={(m) => {
						const next = [...slotModels];
						next[showModalIndex] = m;
						setSlotModels(next);
					}}
					setVariants={(v) => {
						const next = [...slotVariants];
						next[showModalIndex] = v;
						setSlotVariants(next);
					}}
					onSelect={(index, data, fullVariant) => {
						const variant: Variant = {
							_id: fullVariant._id,
							name: fullVariant.variant_name || "", // Map variant_name to name
							slug: fullVariant.variant_slug || "", // Map variant_slug to slug
							brand: {
								name: fullVariant.brand_name,
								slug: fullVariant.brand_slug,
							},
							model: {
								name: fullVariant.model_name,
								featured_image: fullVariant.featured_image || null,
								slug: fullVariant.model_slug,
								detailed_slug: fullVariant.detailed_slug,
								is_discontinued: fullVariant.is_discontinued || false,
								is_upcoming: fullVariant.is_upcoming || false,
							},
							price: fullVariant.price || 0, // Ensure price is a number
						};
						handleSelect(index, data, variant);
					}}
					onClose={() => setShowModalIndex(null)}
					initialSelection={displayCars[showModalIndex]}
					selectedVariantIds={displayVariants.map(v => v?._id).filter(Boolean) as string[]}
				/>
			)}
		</>
	);
}